import FileDownloader from "@/components/FileDownloader/FileDownloader";
import Message from "@/components/Message/Message";
import { PageHeader } from "@/components/PageHeader/PageHeader";
import useTableStateParams from "@/components/Table/hooks/useTableState";
import Table, { Column } from "@/components/Table/Table";
import TableHeader from "@/components/TableHeader/TableHeader";
import { GetData } from "@/utilities/api";
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, message, Progress, Space } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

interface Snapshot {
  id: string;
  progress: number;
  status: string;
  date_created: string;
  type: string;
}

interface SnapshotWithFile extends Snapshot {
  file?: string;
}

export default function Snapshots() {
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: () => axios.post("/snapshots"),
    onSuccess: () => {
      message.success("Snapshot is being created");
      queryClient.invalidateQueries(["snapshots"]);
    },
  });

  const downloadSnapShot = async (snapshot_id: string): Promise<string> => {
    const { data } = await axios.get<SnapshotWithFile>(`/snapshots/${snapshot_id}`);
    return data.file || "";
  };

  const { pagination, onSearchFilterSortPaginate } = useTableStateParams({});

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["snapshots", pagination.page, pagination.pageSize],
    refetchInterval: refetchInterval,
    queryFn: () =>
      axios
        .get<GetData<Snapshot>>("/snapshots", {
          params: { page: pagination.page, page_size: pagination.pageSize },
        })
        .then((data) => data.data),
  });

  const create = () => {
    return createMutation.mutateAsync();
  };

  useEffect(() => {
    const rows = data?.data || [];
    if (rows.length > 0) {
      if (rows[0].status === "In progress" || rows[0].status === "Idle") {
        setRefetchInterval(2000);
      } else {
        setRefetchInterval(false);
      }
    }
  }, [data]);

  const columns: Column<Snapshot>[] = [
    {
      key: "date_created",
      label: "Date",
      renderer: (record) => dayjs(record.date_created).toString(),
    },
    { key: "type", label: "Type" },
    {
      key: "status",
      label: "status",
      renderer: (record) => {
        return (
          <div style={{ width: "80%" }}>
            <Progress
              style={{ opacity: isFetching ? 0.5 : 1 }}
              status={
                record.status === "Ready"
                  ? "success"
                  : record.status === "Failed"
                    ? "exception"
                    : "normal"
              }
              percent={record.status === "Ready" ? 100 : record.progress * 100}
            />
          </div>
        );
      },
    },
    {
      key: "file",
      label: "File",
      renderer: (record) =>
        record.status === "Ready" && (
          <FileDownloader
            fileUrlFn={() => downloadSnapShot(record.id)}
            buttonText={
              <Space>
                <DownloadOutlined />
                Download
              </Space>
            }
          />
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Snapshots"
        description={
          <>
            <span>
              Here you can manage your snapshots. Once you have reported on a year and want to start
              to upload data for a new year you need to snapshot your report for future audit
              purposes. The data in this snapshot will stay untouched and will not be affected if
              you mature and update your data. e.g. a bill of material or impact reference.
            </span>
          </>
        }
      />
      <TableHeader
        left={
          <Button
            loading={createMutation.isLoading}
            onClick={create}
            icon={<PlusOutlined />}
          >
            Create new snapshot
          </Button>
        }
      />
      {refetchInterval && (
        <Message type={"note"}>
          This table refreshes automatically until snapshot creation has been completed. Feel free
          to navigate elsewhere and come back in a few minutes to download your snapshot.
        </Message>
      )}

      <Table
        data={data?.data}
        totalRows={data?.total_count}
        loading={isLoading}
        columns={columns}
        pagination={pagination}
        onSearchFilterSortPaginate={onSearchFilterSortPaginate}
        rowKey={"id"}
      />
    </div>
  );
}
